import React, { useEffect, useRef, useState } from "react";
import { Bold, Italic, List, ListOrdered, Quote, Heading2, Link2, Undo2, Loader2, Wand2, Sparkles, Heart, Network, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { runImageJob } from "@/lib/imageJobs";
import InternalLinkSuggestions, { applyInternalLink } from "@/components/InternalLinkSuggestions";

const ToolbarBtn = ({ onClick, label, children, testId }) => (
  <button
    type="button"
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    aria-label={label}
    data-testid={testId}
    className="inline-flex h-8 w-8 items-center justify-center rounded-md text-[#F7F1E8]/86 transition-colors hover:bg-[var(--tint-pink)] hover:text-[#F7F1E8] active:scale-95"
  >
    {children}
  </button>
);

/**
 * Simple contentEditable-based rich text editor.
 * Emits HTML string via onChange.
 */
export const RichTextEditor = ({ value, onChange, placeholder = "Începe să scrii…", title = "", excludeId = null }) => {
  const ref = useRef(null);
  const lastValue = useRef("");
  const debounceRef = useRef(null);
  const [aiAction, setAiAction] = useState(null);
  const [linkSuggestions, setLinkSuggestions] = useState(null);

  useEffect(() => {
    if (ref.current && (value || "") !== lastValue.current) {
      ref.current.innerHTML = value || "";
      lastValue.current = value || "";
    }
  }, [value]);

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  useEffect(() => {
    // Enter creează <p> (nu <div>) — necesar pentru aliniere justify și drop-cap.
    try {
      document.execCommand("defaultParagraphSeparator", false, "p");
    } catch {
      // unele browsere nu suportă — normalizarea din emit acoperă cazul
    }
  }, []);

  // Normalizează HTML-ul la salvare: textul liber și <div>-urile devin paragrafe <p>.
  const normalizeHtml = (html) => {
    const holder = document.createElement("div");
    holder.innerHTML = html;
    const out = document.createElement("div");
    let para = null;
    const keep = (el) => el.textContent.trim() || el.querySelector("img");
    const flush = () => {
      if (para && keep(para)) out.appendChild(para);
      para = null;
    };
    [...holder.childNodes].forEach((node) => {
      const isBlock = node.nodeType === 1 && /^(P|H1|H2|H3|UL|OL|BLOCKQUOTE|FIGURE|PRE|TABLE)$/.test(node.tagName);
      const isDiv = node.nodeType === 1 && node.tagName === "DIV";
      if (isBlock) {
        flush();
        if (keep(node)) out.appendChild(node.cloneNode(true));
      } else if (isDiv) {
        flush();
        const p = document.createElement("p");
        p.innerHTML = node.innerHTML;
        if (keep(p)) out.appendChild(p);
      } else {
        if (!para) para = document.createElement("p");
        para.appendChild(node.cloneNode(true));
      }
    });
    flush();
    return out.innerHTML;
  };

  const emit = () => {
    clearTimeout(debounceRef.current);
    const html = normalizeHtml(ref.current?.innerHTML || "");
    if (html === lastValue.current) return;
    lastValue.current = html;
    onChange && onChange(html);
  };

  const emitDebounced = () => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(emit, 250);
  };

  // Curăță HTML-ul lipit din Word/Pages/alte aplicații: păstrează doar structura de bază.
  const ALLOWED_TAGS = /^(P|BR|B|STRONG|I|EM|U|A|H2|H3|BLOCKQUOTE|UL|OL|LI|IMG)$/;
  const BLOCK_TO_P = /^(DIV|H1|H4|H5|H6|SECTION|ARTICLE|FIGURE|CENTER|PRE|TABLE|TBODY|THEAD|TR|TD|TH)$/;

  const sanitizePastedHtml = (html) => {
    const doc = new DOMParser().parseFromString(html, "text/html");
    doc.querySelectorAll("style, script, meta, link, title, head, iframe, object, svg").forEach((n) => n.remove());
    const walk = (node) => {
      [...node.childNodes].forEach((child) => {
        if (child.nodeType === 8) {
          child.remove();
          return;
        }
        if (child.nodeType !== 1) return;
        walk(child);
        const tag = child.tagName;
        if (ALLOWED_TAGS.test(tag)) {
          [...child.attributes].forEach((a) => {
            const keep =
              (tag === "A" && a.name === "href") || (tag === "IMG" && (a.name === "src" || a.name === "alt"));
            if (!keep) child.removeAttribute(a.name);
          });
          if (tag === "A") {
            child.setAttribute("rel", "noopener");
            const href = child.getAttribute("href") || "";
            if (!/^(https?:|mailto:|\/)/i.test(href.trim())) child.removeAttribute("href");
          }
        } else if (BLOCK_TO_P.test(tag)) {
          const p = doc.createElement(tag === "H1" ? "h2" : "p");
          while (child.firstChild) p.appendChild(child.firstChild);
          child.replaceWith(p);
        } else {
          // span, font, o:p etc. — se desface, rămâne doar conținutul
          while (child.firstChild) child.parentNode.insertBefore(child.firstChild, child);
          child.remove();
        }
      });
    };
    walk(doc.body);
    doc.body.querySelectorAll("p, h2, h3, li, blockquote").forEach((el) => {
      if (!el.textContent.trim() && !el.querySelector("img")) el.remove();
    });
    return doc.body.innerHTML;
  };

  const escapeHtml = (s) =>
    s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

  // După inserare, browserul poate re-injecta span-uri cu stiluri — le curățăm din DOM-ul editorului.
  const cleanEditorDom = () => {
    const root = ref.current;
    if (!root) return;
    root.querySelectorAll("[style], [class]").forEach((el) => {
      el.removeAttribute("style");
      el.removeAttribute("class");
    });
    root.querySelectorAll("span, font").forEach((el) => {
      while (el.firstChild) el.parentNode.insertBefore(el.firstChild, el);
      el.remove();
    });
  };

  const handlePaste = (e) => {
    const html = e.clipboardData?.getData("text/html");
    const text = e.clipboardData?.getData("text/plain");
    if (!html && !text) return;
    e.preventDefault();
    const clean = html
      ? sanitizePastedHtml(html)
      : escapeHtml(text)
          .split(/\n{2,}/)
          .map((p) => `<p>${p.replace(/\n/g, "<br>")}</p>`)
          .join("");
    if (!clean) return;
    ref.current?.focus();
    document.execCommand("insertHTML", false, clean);
    cleanEditorDom();
    emitDebounced();
  };

  // Imagine AI din paragraful selectat (sau din paragraful în care e cursorul), inserată imediat sub el.
  const generateInlineImage = async () => {
    const sel = window.getSelection();
    if (!sel || !sel.anchorNode || !ref.current?.contains(sel.anchorNode)) {
      toast.error("Pune cursorul în paragraful pentru care vrei imaginea (sau selectează un fragment).");
      return;
    }
    let block = sel.anchorNode.nodeType === 3 ? sel.anchorNode.parentElement : sel.anchorNode;
    while (block && block !== ref.current && !/^(P|H2|H3|BLOCKQUOTE|LI|DIV)$/.test(block.tagName)) block = block.parentElement;
    if (!block || block === ref.current) {
      toast.error("Nu am găsit paragraful. Dă click în text și încearcă din nou.");
      return;
    }
    const selected = sel.toString().trim();
    const text = selected.length >= 40 ? selected : block.textContent.trim();
    if (text.length < 40) {
      toast.error("Paragraful e prea scurt — AI-ul are nevoie de cel puțin câteva fraze.");
      return;
    }
    setAiAction("image");
    try {
      const data = await runImageJob("inline", { text: text.slice(0, 3000), title });
      const figure = document.createElement("p");
      const img = document.createElement("img");
      img.src = data.url;
      img.alt = data.alt || "";
      figure.appendChild(img);
      block.insertAdjacentElement("afterend", figure);
      emit();
      toast.success("Imaginea a fost adăugată sub paragraf.");
    } catch (e) {
      toast.error(formatApiError(e, "Nu am putut genera imaginea"));
    } finally {
      setAiAction(null);
    }
  };

  const runAi = async (action) => {
    const html = ref.current?.innerHTML || "";
    const plain = html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
    if (plain.length < 40) {
      toast.error("Scrie mai întâi câteva rânduri — apoi mă pun pe treabă.");
      return;
    }
    setAiAction(action);
    const snapshot = html;
    try {
      const { data } = await api.post("/admin/editor-assist", { action, content_html: html });
      if (!ref.current || !data?.html) throw new Error("gol");
      ref.current.innerHTML = action === "continue" ? snapshot + data.html : data.html;
      emit();
      const doneMsg = {
        polish: "Textul a fost șlefuit.",
        continue: "Am continuat ideea.",
        personalize: "Textul e acum în vocea ta — citește-l și vezi dacă te regăsești.",
      };
      toast.success(doneMsg[action], {
        duration: 8000,
        action: {
          label: "Anulează",
          onClick: () => {
            if (ref.current) {
              ref.current.innerHTML = snapshot;
              emit();
            }
          },
        },
      });
    } catch (err) {
      toast.error(formatApiError(err, "Asistentul nu a putut ajuta acum. Mai încearcă."));
    } finally {
      setAiAction(null);
    }
  };

  const suggestLinks = async () => {
    const html = ref.current?.innerHTML || "";
    const plain = html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
    if (plain.length < 40) {
      toast.error("Scrie mai întâi câteva rânduri — apoi caut însemnări care se leagă.");
      return;
    }
    setAiAction("links");
    try {
      const { data } = await api.post("/admin/editor-links", { content_html: html, title, exclude_id: excludeId });
      setLinkSuggestions(data?.suggestions || []);
    } catch (err) {
      toast.error(formatApiError(err, "Nu am putut căuta legături acum. Mai încearcă."));
    } finally {
      setAiAction(null);
    }
  };

  const insertLink = (index) => {
    if (!ref.current) return;
    const s = linkSuggestions[index];
    const how = applyInternalLink(ref.current, s);
    emit();
    setLinkSuggestions((prev) => prev.map((x, i) => (i === index ? { ...x, inserted: true } : x)));
    toast.success(how === "wrapped" ? "Linkul a intrat pe expresia din text." : "Am adăugat o propoziție cu linkul la final.");
  };

  const exec = (cmd, arg = null) => {
    ref.current?.focus();
    document.execCommand(cmd, false, arg);
    emit();
  };

  const addLink = () => {
    // eslint-disable-next-line no-alert
    const url = window.prompt("Adresa link-ului (URL)");
    if (url) exec("createLink", url);
  };

  return (
    <div data-testid="rich-editor" className="space-y-3">
      <div className="flex flex-wrap items-center gap-1 rounded-full border border-[color-mix(in_srgb,var(--milk)_18%,transparent)] bg-[#2B1B13] px-2 py-1.5 shadow-sm">
        <ToolbarBtn testId="editor-bold" label="Aldin" onClick={() => exec("bold")}>
          <Bold className="h-4 w-4" strokeWidth={1.6} />
        </ToolbarBtn>
        <ToolbarBtn testId="editor-italic" label="Cursiv" onClick={() => exec("italic")}>
          <Italic className="h-4 w-4" strokeWidth={1.6} />
        </ToolbarBtn>
        <span className="mx-1 h-5 w-px bg-slate-200" />
        <ToolbarBtn testId="editor-h2" label="Titlu" onClick={() => exec("formatBlock", "H2")}>
          <Heading2 className="h-4 w-4" strokeWidth={1.6} />
        </ToolbarBtn>
        <ToolbarBtn testId="editor-quote" label="Citat" onClick={() => exec("formatBlock", "BLOCKQUOTE")}>
          <Quote className="h-4 w-4" strokeWidth={1.6} />
        </ToolbarBtn>
        <ToolbarBtn testId="editor-ul" label="Listă cu marcatori" onClick={() => exec("insertUnorderedList")}>
          <List className="h-4 w-4" strokeWidth={1.6} />
        </ToolbarBtn>
        <ToolbarBtn testId="editor-ol" label="Listă numerotată" onClick={() => exec("insertOrderedList")}>
          <ListOrdered className="h-4 w-4" strokeWidth={1.6} />
        </ToolbarBtn>
        <ToolbarBtn testId="editor-link" label="Adaugă link" onClick={addLink}>
          <Link2 className="h-4 w-4" strokeWidth={1.6} />
        </ToolbarBtn>
        <span className="mx-1 h-5 w-px bg-slate-200" />
        <ToolbarBtn testId="editor-undo" label="Anulează" onClick={() => exec("undo")}>
          <Undo2 className="h-4 w-4" strokeWidth={1.6} />
        </ToolbarBtn>
        <span className="mx-1 h-5 w-px bg-slate-200" />
        <button
          type="button"
          data-testid="editor-ai-polish"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => runAi("polish")}
          disabled={!!aiAction}
          className="inline-flex items-center gap-1.5 rounded-full border border-[color-mix(in_srgb,var(--accent)_30%,transparent)] px-3 py-1.5 text-[11px] font-medium uppercase tracking-[0.12em] text-[var(--accent)] transition-colors hover:bg-[var(--tint-pink)] disabled:opacity-50"
        >
          {aiAction === "polish" ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.6} />
          ) : (
            <Wand2 className="h-3.5 w-3.5" strokeWidth={1.6} />
          )}
          Șlefuiește textul
        </button>
        <button
          type="button"
          data-testid="editor-ai-continue"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => runAi("continue")}
          disabled={!!aiAction}
          className="inline-flex items-center gap-1.5 rounded-full border border-[color-mix(in_srgb,var(--teal)_35%,transparent)] px-3 py-1.5 text-[11px] font-medium uppercase tracking-[0.12em] text-[var(--teal-dark)] transition-colors hover:bg-[var(--tint-mint)] disabled:opacity-50"
        >
          {aiAction === "continue" ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.6} />
          ) : (
            <Sparkles className="h-3.5 w-3.5" strokeWidth={1.6} />
          )}
          Continuă ideea
        </button>
        <button
          type="button"
          data-testid="editor-ai-personalize"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => runAi("personalize")}
          disabled={!!aiAction}
          className="inline-flex items-center gap-1.5 rounded-full border border-[color-mix(in_srgb,var(--milk)_25%,transparent)] px-3 py-1.5 text-[11px] font-medium uppercase tracking-[0.12em] text-[var(--milk)] transition-colors hover:bg-[var(--tint-butter)] disabled:opacity-50"
        >
          {aiAction === "personalize" ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.6} />
          ) : (
            <Heart className="h-3.5 w-3.5" strokeWidth={1.6} />
          )}
          Mai incisiv
        </button>
        <button
          type="button"
          data-testid="editor-ai-links"
          onMouseDown={(e) => e.preventDefault()}
          onClick={suggestLinks}
          disabled={!!aiAction}
          className="inline-flex items-center gap-1.5 rounded-full border border-[color-mix(in_srgb,var(--teal)_35%,transparent)] px-3 py-1.5 text-[11px] font-medium uppercase tracking-[0.12em] text-[var(--teal-dark)] transition-colors hover:bg-[var(--tint-mint)] disabled:opacity-50"
        >
          {aiAction === "links" ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.6} />
          ) : (
            <Network className="h-3.5 w-3.5" strokeWidth={1.6} />
          )}
          Legături interne
        </button>
        <button
          type="button"
          data-testid="editor-ai-image"
          onMouseDown={(e) => e.preventDefault()}
          onClick={generateInlineImage}
          disabled={!!aiAction}
          className="inline-flex items-center gap-1.5 rounded-full border border-[color-mix(in_srgb,var(--accent)_30%,transparent)] px-3 py-1.5 text-[11px] font-medium uppercase tracking-[0.12em] text-[var(--accent)] transition-colors hover:bg-[var(--tint-pink)] disabled:opacity-50"
          title="Generează o imagine AI din paragraful selectat și o pune sub el"
        >
          {aiAction === "image" ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.6} />
          ) : (
            <ImagePlus className="h-3.5 w-3.5" strokeWidth={1.6} />
          )}
          Imagine din paragraf
        </button>
      </div>

      {linkSuggestions !== null && (
        <InternalLinkSuggestions suggestions={linkSuggestions} onInsert={insertLink} onClose={() => setLinkSuggestions(null)} />
      )}

      <div
        ref={ref}
        data-testid="rich-editor-area"
        className="bloom-editor"
        contentEditable
        suppressContentEditableWarning
        data-placeholder={placeholder}
        onInput={emitDebounced}
        onPaste={handlePaste}
        onBlur={emit}
      />
    </div>
  );
};

export default RichTextEditor;
